<template>
  <div class="editor-container">
    <!-- Всплывающее меню форматирования -->
    <bubble-menu
      class="bubble-menu"
      :editor="editor"
      v-if="editor"
      :tippy-options="{ duration: 100, placement: 'top' }"
    >
      <button
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
        title="Жирный"
      >
        <strong>B</strong>
      </button>
      <button
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
        title="Курсив"
      >
        <em>I</em>
      </button>
      <button
        @click="openLinkModal"
        :class="{ 'is-active': editor.isActive('link') }"
        title="Ссылка"
      >
        <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M9.16488 17.6505C8.92513 17.8743 8.73958 18.0241 8.54996 18.1336C7.62175 18.6695 6.47816 18.6695 5.54996 18.1336C5.20791 17.9361 4.87912 17.6073 4.22153 16.9498C3.56394 16.2922 3.23514 15.9634 3.03767 15.6213C2.50177 14.6931 2.50177 13.5495 3.03767 12.6213C3.23514 12.2793 3.56394 11.9505 4.22153 11.2929L7.04996 8.46448C7.70755 7.80689 8.03634 7.47809 8.37838 7.28062C9.30659 6.74472 10.4502 6.74472 11.3784 7.28061C11.7204 7.47809 12.0492 7.80689 12.7068 8.46448C13.3644 9.12207 13.6932 9.45086 13.8907 9.7929C14.4266 10.7211 14.4266 11.8647 13.8907 12.7929C13.7812 12.9825 13.6314 13.1681 13.4075 13.4078M10.5919 10.5922C10.368 10.8319 10.2182 11.0175 10.1087 11.2071C9.57284 12.1353 9.57284 13.2789 10.1087 14.2071C10.3062 14.5492 10.635 14.878 11.2926 15.5355C11.9502 16.1931 12.279 16.5219 12.621 16.7194C13.5492 17.2553 14.6928 17.2553 15.621 16.7194C15.9631 16.5219 16.2919 16.1931 16.9495 15.5355L19.7779 12.7071C20.4355 12.0495 20.7643 11.7207 20.9617 11.3787C21.4976 10.4505 21.4976 9.30689 20.9617 8.37869C20.7643 8.03665 20.4355 7.70785 19.7779 7.05026C19.1203 6.39267 18.7915 6.06388 18.4495 5.8664C17.5212 5.3305 16.3777 5.3305 15.4495 5.8664C15.2598 5.97588 15.0743 6.12571 14.8345 6.34955" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path> </g></svg>
      </button>
    </bubble-menu>

    <!-- Модальное окно для ссылок -->
    <div v-if="linkModalVisible" class="link-modal" @click.self="closeLinkModal">
      <div class="link-modal-content">
        <input
          v-model="linkUrl"
          @keyup.enter="saveLink"
          placeholder="https://example.com"
          ref="linkInput"
        />
        <div class="link-modal-buttons">
          <button @click="saveLink" class="save-btn">Применить</button>
          <button @click="removeLink" class="remove-btn">Удалить</button>
          <button @click="closeLinkModal" class="cancel-btn">Отмена</button>
        </div>
      </div>
    </div>

    <!-- Область редактора с обработчиками -->
    <div class="editor-wrapper" @click="handleClick" @dblclick="handleDblClick">
      <editor-content :editor="editor" class="editor" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

export default {
  name: 'TiptapEditor',
  components: {
    EditorContent,
    BubbleMenu
  },

  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Введите текст...'
    },
    editable: {
      type: Boolean,
      default: true
    }
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  data() {
    return {
      editor: null,
      linkModalVisible: false,
      linkUrl: ''
    }
  },

  watch: {
    modelValue(value) {
      if (this.editor && value !== this.editor.getHTML()) {
        this.editor.commands.setContent(value, false)
      }
    },
    editable(value) {
      this.editor?.setEditable(value)
    }
  },

  methods: {
    handleClick(event) {
      if (!event?.target) return
      
      try {
        const linkElement = event.target.closest('a')
        if (linkElement && (event.ctrlKey || event.metaKey)) {
          window.open(linkElement.href, '_blank')
          event.preventDefault()
        }
      } catch (error) {
        console.error('Ошибка обработки клика:', error)
      }
    },

    handleDblClick(event) {
      if (!event?.target) return
      
      try {
        const linkElement = event.target.closest('a')
        if (linkElement) {
          window.open(linkElement.href, '_blank')
          event.preventDefault()
        }
      } catch (error) {
        console.error('Ошибка обработки клика:', error)
      }
    },

    openLinkModal() {
      if (this.editor.isActive('link')) {
        this.linkUrl = this.editor.getAttributes('link').href || ''
      } else {
        this.linkUrl = 'https://'
      }
      this.linkModalVisible = true
      this.$nextTick(() => {
        this.$refs.linkInput?.focus()
        this.$refs.linkInput?.select()
      })
    },

    closeLinkModal() {
      this.linkModalVisible = false
      this.editor?.commands.focus()
    },

    saveLink() {
      if (!this.linkUrl) return

      let finalUrl = this.linkUrl.trim()
      if (!/^https?:\/\//i.test(finalUrl)) {
        finalUrl = 'http://' + finalUrl
      }

      this.editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: finalUrl })
        .run()

      this.closeLinkModal()
    },

    removeLink() {
      this.editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run()
      this.closeLinkModal()
    }
  },

  mounted() {
    this.editor = new Editor({
      content: this.modelValue,
      editable: this.editable,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
            class: 'editor-link'
          }
        }),
        Placeholder.configure({
          placeholder: ({ node }) => {
            if (node.type.name === 'paragraph' && node.childCount === 0) {
              return this.placeholder
            }
            return ''
          }
        })
      ],
      onUpdate: () => {
        this.$emit('update:modelValue', this.editor.getHTML())
      },
      onBlur: () => {
        this.$emit('blur')
      },
      onFocus: () => {
        this.$emit('focus')
      }
    })
  },

  beforeUnmount() {
    this.editor?.destroy()
  }
}
</script>

<style>
.editor-container {
  position: relative;
}

.editor-wrapper {
  position: relative;
}

.editor {
  box-shadow: inset 0 0 0px 1px var(--color-primary-200);
  color: var(--color-primary-800);
  border-radius: 8px;
  padding: var(--base-value-2);
  min-height: 90px;
  background: white;
  transition: border-color 0.2s;
  font-size: var(--font-size-m) !important;
  max-height: 160px;
  overflow-y: auto;
  border: none;
}

.editor:focus-within {
  box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.25);
  outline: none;
}

/* Стили для всплывающего меню */
.bubble-menu {
  display: flex;
  gap: 4px;
  background-color: var(--color-primary-600);
  padding: 2px;
  border-radius: 4px;
}

.bubble-menu button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  color: #f8fafc;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.bubble-menu button:hover {
  background-color: #334155;
}

.bubble-menu button.is-active {
  background-color: #3b82f6;
  color: white;
}

/* Стили для модального окна ссылок */
.link-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.link-modal-content {
  background: white;
  padding: 20px;
  border-radius: 12px;
  width: 320px;
  max-width: 90%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.link-modal-content input {
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
}

.link-modal-content input:focus {
  border-color: #3b82f6;
  outline: none;
}

.link-modal-buttons {
  display: flex;
  gap: 8px;
}

.link-modal-buttons button {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.save-btn {
  background: #3b82f6;
  color: white;
}

.remove-btn {
  background: #ef4444;
  color: white;
}

.cancel-btn {
  background: #f1f5f9;
  color: #64748b;
}

/* Стили для контента редактора */
.ProseMirror {
  min-height: 90px;
  outline: none;
}

.ProseMirror p.is-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #94a3b8;
  pointer-events: none;
  height: 0;
}

/* Стили для ссылок */
.editor-link {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}

.editor-link:hover {
  text-decoration: none;
}
</style>